import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { listSystemUsers } from "../../services/systemUserService";
import { listPermissions } from "../../services/systemRoleService";
import { listMembers } from "../../services/memberService";
import { listBookings } from "../../services/bookingService";
import { listFacilities } from "../../services/facilityService";
import { listClubs } from "../../services/clubService";
import { listMemberRegistrationPayments, listClubRegistrationPayments } from "../../services/paymentService";
import { listRenewalPayments } from "../../services/renewalService";
import { listClubRenewalPayments } from "../../services/clubRenewalService";
import { listFees } from "../../services/feeService";
import { TOKENS, Icon, ICONS, StatCard, Spinner } from "../../components/common/DashboardUI";
import campusGatePhoto from "../../assets/dashboard/campus-gate.jpg";
import { FEATURE_FLAGS } from "../../config/featureFlags";

// Back-office landing page. Shows a welcome header, top-level counts, a
// monthly System Overview + Activity chart, and a real "recent activity"
// feed. Every fetch below is guarded by the same "view_*" permission the
// resource's own admin page is gated by (see
// adminNav.js), so a limited role just sees fewer sections rather than a
// wall of 403s — same graceful-degradation approach the previous version of
// this page used.
//
// Two categorical series colors used throughout (the Activity chart, dots in
// Recent Activities): TOKENS.PRIMARY (#1d4ed8, "Bookings") and #16a34a
// ("Payments") — validated as a CVD-safe pair via the dataviz skill's
// scripts/validate_palette.js (worst-pair ΔE 31.8 CVD / 35.6 normal-vision,
// both well clear of the 8/15 targets).
const BOOKINGS_COLOR = TOKENS.PRIMARY;
const PAYMENTS_COLOR = "#16a34a";

// Static illustration icons (colored badge background baked in) for the top
// overview cards and the System Overview stats — sourced from the provided
// design reference, not the app's own thin-line ICONS path set.
const DASH_ICONS = {
  systemUsers: "/images/dashboard/icons/system-users.jpg",
  activeMembers: "/images/dashboard/icons/active-members.jpg",
  clubs: "/images/dashboard/icons/clubs.jpg",
  permissions: "/images/dashboard/icons/permissions.jpg",
  totalMembers: "/images/dashboard/icons/total-members.jpg",
  totalBookings: "/images/dashboard/icons/total-bookings.jpg",
  totalRevenue: "/images/dashboard/icons/total-revenue.jpg",
};

const money = (v) => `LKR ${Math.round(Number(v) || 0).toLocaleString("en-LK")}`;

const monthKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabelOf = (d) => d.toLocaleString("en-US", { month: "long", year: "numeric" });
const inMonth = (dateStr, key) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return !Number.isNaN(d.getTime()) && monthKeyOf(d) === key;
};
const previousMonthKey = (key) => {
  const [y, m] = key.split("-").map(Number);
  return monthKeyOf(new Date(y, m - 2, 1));
};

// Signed "↑N% / ↓N% vs previous month" — "New" when there's nothing to
// compare against (previous month was zero but this month isn't), "—" when
// both months are zero.
function trendInfo(current, previous) {
  if (previous === 0) return current === 0 ? { label: "—", up: null } : { label: "New", up: true };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { label: `${pct >= 0 ? "↑" : "↓"}${Math.abs(pct)}%`, up: pct >= 0 };
}

// ── Count-up animation for Overview-card numbers ────────────────────────────
// Animates a displayed number from its previous on-screen value up to
// `target` via requestAnimationFrame (ease-out cubic, ~900ms) whenever
// `target` changes. The cards that use this only mount once `loading` goes
// false with the real fetched counts already in hand, so this hook's first
// run animates 0 → actual value — a genuine page-load count-up, not a fake
// timer. Purely a display effect: only the rendered number is animated,
// never the underlying fetched data it's derived from.
function useCountUp(target, duration = 900) {
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(0);
  useEffect(() => {
    const to = typeof target === "number" && !Number.isNaN(target) ? target : 0;
    const from = displayRef.current;
    if (from === to) return;
    let rafId;
    let startTs = null;
    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (to - from) * eased;
      displayRef.current = current;
      setDisplay(current);
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);
  return display;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(dateStr).toLocaleDateString();
}

const fullMemberName = (m) => [m?.title, m?.name_denoted_by_initials, m?.lastname].filter(Boolean).join(" ") || "—";
const fullBookingPayerName = (b) => [b?.title, b?.initials, b?.lastname].filter(Boolean).join(" ") || "—";

const todayLongLabel = () =>
  new Date().toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

// ── Welcome card — dark navy banner with the greeting + real session info on
// the left, and the university campus photo filling the right side (hidden
// below lg, where there's no room for it beside the text without cramping
// both). Mirrors the hero-banner treatment already used on the member/coach
// dashboards (see CLAUDE.md), just in the admin portal's navy palette
// instead of their light gradient. ──────────────────────────────────────────
function WelcomeCard({ firstName, role, permissionCount }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-6 shadow-sm"
      style={{ background: `linear-gradient(120deg, ${TOKENS.SIDEBAR_TOP} 0%, ${TOKENS.SIDEBAR_BOTTOM} 100%)` }}
    >
      <div className="flex flex-col lg:flex-row items-stretch">
        <div className="flex-1 min-w-0 p-6 sm:p-8 flex flex-col justify-center">
          <h1 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2 text-white">
            <span aria-hidden="true">👋</span> Welcome, {firstName}
          </h1>
          <p className="text-sm text-blue-100/80 mt-1">
            Signed in as <span className="font-semibold text-white">{role}</span> ·{" "}
            {permissionCount} permission{permissionCount === 1 ? "" : "s"}
          </p>
          <p className="text-xs text-blue-200/70 mt-3 flex items-center gap-2">
            <Icon path={ICONS.bookings} className="w-4 h-4 flex-shrink-0" stroke="currentColor" width={1.8} />
            {todayLongLabel()}
          </p>
        </div>
        <div className="relative lg:w-[45%] min-h-[160px]">
          <img src={campusGatePhoto} alt="University of Sri Jayewardenepura" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${TOKENS.SIDEBAR_TOP} 0%, rgba(23,37,84,0) 45%)` }} />
          <p
            className="absolute top-4 right-5 text-white font-semibold italic text-sm text-right leading-snug"
            style={{ fontFamily: "Georgia, serif", textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            Stronger
            <br />
            Healthier
            <br />
            Together
          </p>
          <div className="absolute bottom-4 right-5 flex items-center gap-1.5" aria-hidden="true">
            <span className="w-1.5 h-1.5 rounded-full bg-white/50" />
            <span className="w-4 h-1.5 rounded-full bg-white" />
            <span className="w-1.5 h-1.5 rounded-full bg-white/50" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── One System Overview stat (Members / Bookings / Revenue this month) ─────
function OverviewStat({ icon, iconSrc, accent, label, value, trend }) {
  return (
    <div className="rounded-xl border border-gray-100 p-4 flex items-center gap-3">
      {iconSrc ? (
        <img src={iconSrc} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: TOKENS.LIGHT }}>
          <Icon path={icon} className="w-5 h-5" stroke={accent} width={2} />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="font-extrabold text-lg truncate" style={{ color: TOKENS.NAVY }}>
          {value}
        </p>
        {trend && (
          <p className={`text-[11px] font-semibold ${trend.up === null ? "text-gray-400" : trend.up ? "text-green-600" : "text-red-600"}`}>
            {trend.label}
            {trend.up !== null && <span className="text-gray-400 font-normal"> vs previous month</span>}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Activity Overview — daily "bookings created" vs "payments received"
// count for the selected month, as a lightweight inline SVG line chart (no
// charting library in this project). bookingsByDay/paymentsByDay are real
// per-day counts computed from the fetched bookings/payments (see the
// bookingsByDay/paymentsByDay useMemo in AdminDashboard below) — never
// hardcoded or randomized, and recomputed (so this chart redraws) whenever
// the selected month changes. Two-series categorical color pair
// (BOOKINGS_COLOR / PAYMENTS_COLOR) validated via the dataviz skill. Ships a
// hover crosshair + tooltip per the skill's interaction guidance, since an
// SVG chart with no hover state reads as a static image. ───────────────────
function ActivityChart({ bookingsByDay, paymentsByDay }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const days = bookingsByDay.length;

  // Left-to-right "draw in" reveal (0 → 1, ease-out) — purely a load-in
  // animation for the two lines/end-markers below, re-run whenever the
  // day-by-day arrays change identity (a fresh month's real data). The
  // plotted values themselves are never touched by this.
  const [reveal, setReveal] = useState(0);
  useEffect(() => {
    setReveal(0);
    let rafId;
    let startTs = null;
    const duration = 900;
    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      setReveal(1 - Math.pow(1 - p, 3));
      if (p < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [bookingsByDay, paymentsByDay]);
  const W = 680;
  const H = 220;
  const padL = 34;
  const padR = 12;
  const padT = 14;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const rawMax = Math.max(1, ...bookingsByDay, ...paymentsByDay);
  // Round the axis ceiling up to a "nice" step (5/10/25/50/100…) so ticks
  // read as clean numbers rather than an arbitrary max like "37".
  const niceMax = (n) => {
    const step = n <= 10 ? 2 : n <= 25 ? 5 : n <= 50 ? 10 : n <= 100 ? 25 : Math.ceil(n / 100) * 100;
    return Math.ceil(n / step) * step;
  };
  const yMax = niceMax(rawMax);

  const xAt = (i) => padL + (days === 1 ? 0 : (i / (days - 1)) * innerW);
  const yAt = (v) => padT + innerH - (v / yMax) * innerH;

  const pathFor = (arr) => arr.map((v, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(" ");

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  // Sparse x labels — every ~5th day plus the last, so labels never collide.
  const xLabelStep = Math.max(1, Math.ceil(days / 7));

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 220 }} role="img" aria-label="Bookings and payments this month">
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={yAt(t)} y2={yAt(t)} stroke="#e5e7eb" strokeWidth={1} />
            <text x={padL - 8} y={yAt(t) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
              {t}
            </text>
          </g>
        ))}

        {bookingsByDay.map((_, i) =>
          i % xLabelStep === 0 || i === days - 1 ? (
            <text key={i} x={xAt(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#9ca3af">
              {i + 1}
            </text>
          ) : null
        )}

        <defs>
          <clipPath id="activity-chart-reveal">
            <rect x={padL} y={0} width={Math.max(0, reveal * innerW)} height={H} />
          </clipPath>
        </defs>
        <g clipPath="url(#activity-chart-reveal)">
          <path d={pathFor(bookingsByDay)} fill="none" stroke={BOOKINGS_COLOR} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          <path d={pathFor(paymentsByDay)} fill="none" stroke={PAYMENTS_COLOR} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

          {/* End-of-line markers with a surface ring, per the mark spec. */}
          <circle cx={xAt(days - 1)} cy={yAt(bookingsByDay[days - 1])} r={4} fill={BOOKINGS_COLOR} stroke="#fff" strokeWidth={2} />
          <circle cx={xAt(days - 1)} cy={yAt(paymentsByDay[days - 1])} r={4} fill={PAYMENTS_COLOR} stroke="#fff" strokeWidth={2} />
        </g>

        {hoverIdx != null && (
          <>
            <line x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={padT} y2={H - padB} stroke="#cbd5e1" strokeWidth={1} />
            <circle cx={xAt(hoverIdx)} cy={yAt(bookingsByDay[hoverIdx])} r={4} fill={BOOKINGS_COLOR} stroke="#fff" strokeWidth={2} />
            <circle cx={xAt(hoverIdx)} cy={yAt(paymentsByDay[hoverIdx])} r={4} fill={PAYMENTS_COLOR} stroke="#fff" strokeWidth={2} />
          </>
        )}

        {/* Invisible per-day hit strips driving the hover state. */}
        {bookingsByDay.map((_, i) => {
          const stripW = innerW / days;
          return (
            <rect
              key={i}
              x={padL + i * stripW}
              y={padT}
              width={stripW}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
            />
          );
        })}
      </svg>

      {hoverIdx != null && (
        <div
          className="absolute top-1 bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-xs pointer-events-none"
          style={{ left: `${Math.min(78, Math.max(2, (xAt(hoverIdx) / W) * 100))}%` }}
        >
          <p className="font-semibold text-gray-700 mb-1">Day {hoverIdx + 1}</p>
          <p className="flex items-center gap-1.5 text-gray-500">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: BOOKINGS_COLOR }} />
            Bookings: <span className="font-semibold text-gray-700">{bookingsByDay[hoverIdx]}</span>
          </p>
          <p className="flex items-center gap-1.5 text-gray-500">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: PAYMENTS_COLOR }} />
            Payments: <span className="font-semibold text-gray-700">{paymentsByDay[hoverIdx]}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ── Quick Actions row — shortcuts into the four most common admin modules,
// each gated by the same permission that already controls that module's own
// ADMIN_NAV visibility, so a limited role just sees fewer tiles. ───────────
function QuickActionCard({ icon, color, bg, title, subtitle, to }) {
  return (
    <Link to={to} className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
        <Icon path={icon} className="w-5 h-5" stroke={color} width={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold truncate" style={{ color: TOKENS.NAVY }}>
          {title}
        </p>
        <p className="text-xs text-gray-400 truncate">{subtitle}</p>
      </div>
      <Icon path={ICONS.chevron} className="w-4 h-4 flex-shrink-0 -rotate-90" stroke="#9ca3af" width={2} />
    </Link>
  );
}

function ActivityRow({ icon, color, bg, title, subtitle, at }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
          <Icon path={icon} className="w-4 h-4" stroke={color} width={2} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
            {title}
          </p>
          <p className="text-xs text-gray-400 truncate">{subtitle}</p>
        </div>
      </div>
      <span className="text-[11px] text-gray-400 flex-shrink-0 whitespace-nowrap">{timeAgo(at)}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, role, permissions, can } = useAuth();

  const [loading, setLoading] = useState(true);
  const [systemUsers, setSystemUsers] = useState([]);
  const [permissionsCatalog, setPermissionsCatalog] = useState([]);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [memberRegPayments, setMemberRegPayments] = useState([]);
  const [clubRegPayments, setClubRegPayments] = useState([]);
  const [renewalPayments, setRenewalPayments] = useState([]);
  const [clubRenewalPayments, setClubRenewalPayments] = useState([]);
  const [fees, setFees] = useState([]);

  useEffect(() => {
    const jobs = [];
    if (can("view_system_users")) jobs.push(listSystemUsers().then(setSystemUsers).catch(() => {}));
    if (can("view_roles")) jobs.push(listPermissions().then(setPermissionsCatalog).catch(() => {}));
    if (can("view_members")) jobs.push(listMembers().then(setMembers).catch(() => {}));
    if (can("view_bookings")) jobs.push(listBookings().then(setBookings).catch(() => {}));
    if (can("view_facilities")) jobs.push(listFacilities().then(setFacilities).catch(() => {}));
    if (can("view_clubs")) jobs.push(listClubs().then(setClubs).catch(() => {}));
    if (can("view_payments")) {
      jobs.push(listMemberRegistrationPayments().then(setMemberRegPayments).catch(() => {}));
      jobs.push(listClubRegistrationPayments().then(setClubRegPayments).catch(() => {}));
      jobs.push(listRenewalPayments().then(setRenewalPayments).catch(() => {}));
      jobs.push(listClubRenewalPayments().then(setClubRenewalPayments).catch(() => {}));
    }
    if (can("view_fees")) jobs.push(listFees().then(setFees).catch(() => {}));
    Promise.all(jobs).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can]);

  // One flat list of every real "money received" event this app can see,
  // normalized to {amount, paidAt}. Membership renewals are the one
  // exception to using `payment_status === "Paid"`: MembershipRenewalController
  // ::formatRenewal() never returns a trustworthy status string for this
  // resource (see renewalService.js / CLAUDE.md), so `paid_at` being set is
  // the only reliable "this is actually paid" signal there. Fees have no
  // "Paid" concept at all — a row in listFees() is already realized income
  // (see feeService.js) — so every row counts, dated by created_at. ID card
  // reprint payments are NOT included: the backend has no admin-wide listing
  // endpoint for them (only a Pending-only approvals queue), so there is no
  // way to sum that revenue without per-record lookups.
  const paidTransactions = useMemo(() => {
    const rows = [];
    bookings.forEach((b) => {
      if (b.payment_status === "Paid" && b.paid_at) rows.push({ amount: Number(b.paid_amount) || 0, paidAt: b.paid_at });
    });
    memberRegPayments.forEach((p) => {
      if (p.payment_status === "Paid" && p.paid_at) rows.push({ amount: Number(p.paid_amount) || 0, paidAt: p.paid_at });
    });
    clubRegPayments.forEach((p) => {
      if (p.payment_status === "Paid" && p.paid_at) rows.push({ amount: Number(p.paid_amount) || 0, paidAt: p.paid_at });
    });
    renewalPayments.forEach((p) => {
      if (p.paid_at) rows.push({ amount: Number(p.paid_amount) || 0, paidAt: p.paid_at });
    });
    clubRenewalPayments.forEach((p) => {
      if (p.payment_status === "Paid" && p.paid_at) rows.push({ amount: Number(p.paid_amount) || 0, paidAt: p.paid_at });
    });
    fees.forEach((f) => {
      if (f.created_at) rows.push({ amount: Number(f.fee) || 0, paidAt: f.created_at });
    });
    return rows;
  }, [bookings, memberRegPayments, clubRegPayments, renewalPayments, clubRenewalPayments, fees]);

  const hasRevenueSource = can("view_bookings") || can("view_payments") || can("view_fees");
  const showOverview = can("view_members") || can("view_bookings") || hasRevenueSource;

  // Last 6 calendar months, newest first, for the System Overview / Activity
  // Overview month picker — everything below is filtered client-side from
  // the already-fully-fetched arrays above (none of these endpoints are
  // paginated), so switching months needs no extra API calls.
  const monthOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return { key: monthKeyOf(d), label: monthLabelOf(d) };
    });
  }, []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.key);
  const prevMonth = previousMonthKey(selectedMonth);

  const membersThis = members.filter((m) => inMonth(m.created_at, selectedMonth)).length;
  const membersPrev = members.filter((m) => inMonth(m.created_at, prevMonth)).length;
  const bookingsThis = bookings.filter((b) => inMonth(b.created_at, selectedMonth)).length;
  const bookingsPrev = bookings.filter((b) => inMonth(b.created_at, prevMonth)).length;
  const revenueThis = paidTransactions.filter((t) => inMonth(t.paidAt, selectedMonth)).reduce((s, t) => s + t.amount, 0);
  const revenuePrev = paidTransactions.filter((t) => inMonth(t.paidAt, prevMonth)).reduce((s, t) => s + t.amount, 0);

  const daysInSelectedMonth = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  }, [selectedMonth]);

  const { bookingsByDay, paymentsByDay } = useMemo(() => {
    const b = Array(daysInSelectedMonth).fill(0);
    bookings.forEach((row) => {
      if (!inMonth(row.created_at, selectedMonth)) return;
      b[new Date(row.created_at).getDate() - 1] += 1;
    });
    const p = Array(daysInSelectedMonth).fill(0);
    paidTransactions.forEach((row) => {
      if (!inMonth(row.paidAt, selectedMonth)) return;
      p[new Date(row.paidAt).getDate() - 1] += 1;
    });
    return { bookingsByDay: b, paymentsByDay: p };
  }, [bookings, paidTransactions, selectedMonth, daysInSelectedMonth]);

  // Newest real record from each resource this role can see, merged into one
  // timeline. Clubs are deliberately excluded — listClubs() has no
  // created_at field to sort by (see facilityService.js sibling comment
  // style / prior investigation), so there's no reliable "newest club".
  const recentActivities = useMemo(() => {
    const items = [];
    const newest = (rows, dateField) =>
      rows.length ? [...rows].sort((a, b) => new Date(b[dateField] || 0) - new Date(a[dateField] || 0))[0] : null;

    const m = newest(members, "created_at");
    if (m?.created_at) items.push({ icon: ICONS.user, color: TOKENS.PRIMARY, bg: TOKENS.LIGHT, title: "New member registered", subtitle: fullMemberName(m), at: m.created_at });

    const b = newest(bookings, "created_at");
    if (b?.created_at) items.push({ icon: ICONS.bookings, color: "#c2410c", bg: "#ffedd5", title: "New booking created", subtitle: fullBookingPayerName(b), at: b.created_at });

    const t = newest(paidTransactions, "paidAt");
    if (t?.paidAt) items.push({ icon: ICONS.payments, color: "#15803d", bg: "#dcfce7", title: "Payment received", subtitle: money(t.amount), at: t.paidAt });

    const u = newest(systemUsers, "created_at");
    if (u?.created_at) items.push({ icon: ICONS.userCheck, color: "#7c3aed", bg: "#ede9fe", title: "New system user added", subtitle: u.name || "—", at: u.created_at });

    const f = newest(facilities, "created_at");
    if (f?.created_at) items.push({ icon: ICONS.facility, color: TOKENS.NAVY, bg: TOKENS.LIGHT, title: "New facility added", subtitle: f.facility_name || "—", at: f.created_at });

    return items.sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 5);
  }, [members, bookings, paidTransactions, systemUsers, facilities]);

  // "Active" here mirrors the same substring check statusColor() uses for
  // every other status pill in the app (member_status is a free-text string
  // from the backend, e.g. "Active"/"Expired").
  const activeMembersCount = members.filter((m) => String(m.member_status || "").toLowerCase().includes("active")).length;

  // Animated (count-up) mirrors of the raw counts above — display only, the
  // `show`/routing/label logic below still reads the real numbers.
  const animatedSystemUsers = Math.round(useCountUp(systemUsers.length));
  const animatedActiveMembers = Math.round(useCountUp(activeMembersCount));
  const animatedClubsCount = Math.round(useCountUp(clubs.length));
  const animatedPermissionsCount = Math.round(useCountUp(permissionsCatalog.length));
  const animatedMembersThis = Math.round(useCountUp(membersThis));
  const animatedBookingsThis = Math.round(useCountUp(bookingsThis));
  const animatedRevenueThis = useCountUp(revenueThis);

  const topCards = [
    { show: can("view_system_users"), label: "System Users", value: animatedSystemUsers, to: "/admin/users", iconSrc: DASH_ICONS.systemUsers },
    { show: can("view_members"), label: "Active Members", value: animatedActiveMembers, to: "/admin/members", iconSrc: DASH_ICONS.activeMembers },
    { show: can("view_clubs"), label: "Clubs", value: animatedClubsCount, to: "/admin/clubs", iconSrc: DASH_ICONS.clubs },
    { show: can("view_roles"), label: "Permissions", value: animatedPermissionsCount, to: "/admin/roles", iconSrc: DASH_ICONS.permissions },
  ].filter((c) => c.show);

  const quickActions = [
    { show: can("view_members"), icon: ICONS.user, color: "#15803d", bg: "#dcfce7", title: "Manage Members", subtitle: "View and manage member details", to: "/admin/members" },
    { show: can("view_facilities"), icon: ICONS.facility, color: "#7c3aed", bg: "#ede9fe", title: "Facility Overview", subtitle: "Check facility status and bookings", to: "/admin/facilities" },
    { show: FEATURE_FLAGS.bookingUI && can("view_bookings"), icon: ICONS.bookings, color: "#c2410c", bg: "#ffedd5", title: "View Bookings", subtitle: "Manage facility bookings", to: "/admin/bookings" },
    { show: can("view_payments"), icon: ICONS.payments, color: TOKENS.PRIMARY, bg: TOKENS.LIGHT, title: "Payments & Fees", subtitle: "Track payments and dues", to: "/admin/payments" },
  ].filter((a) => a.show);

  return (
    <div>
      <WelcomeCard firstName={user?.name?.split(" ")[0] || "Operator"} role={role} permissionCount={permissions.length} />

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center py-16 gap-3">
          <Spinner />
          <p className="text-sm text-gray-400">Loading dashboard…</p>
        </div>
      ) : (
        <>
          {topCards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {topCards.map((card) => (
                <Link key={card.label} to={card.to} className="block hover:opacity-90 transition">
                  <StatCard label={card.label} value={card.value} iconSrc={card.iconSrc} />
                </Link>
              ))}
            </div>
          )}

          {showOverview && (
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                  System Overview
                </h2>
                <select
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {monthOptions.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {can("view_members") && (
                  <OverviewStat iconSrc={DASH_ICONS.totalMembers} label="Total Members" value={animatedMembersThis} trend={trendInfo(membersThis, membersPrev)} />
                )}
                {can("view_bookings") && (
                  <OverviewStat iconSrc={DASH_ICONS.totalBookings} label="Total Bookings" value={animatedBookingsThis} trend={trendInfo(bookingsThis, bookingsPrev)} />
                )}
                {hasRevenueSource && (
                  <OverviewStat iconSrc={DASH_ICONS.totalRevenue} label="Total Revenue" value={money(animatedRevenueThis)} trend={trendInfo(revenueThis, revenuePrev)} />
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {(can("view_bookings") || hasRevenueSource) && (
                <div className="bg-white rounded-2xl shadow-sm p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h2 className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                      Activity Overview
                    </h2>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: BOOKINGS_COLOR }} />
                        Bookings
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: PAYMENTS_COLOR }} />
                        Payments
                      </span>
                    </div>
                  </div>
                  <ActivityChart bookingsByDay={bookingsByDay} paymentsByDay={paymentsByDay} />
                </div>
              )}
            </div>

            <div className="space-y-6">
              {recentActivities.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-5">
                  <h2 className="text-sm font-bold mb-1" style={{ color: TOKENS.NAVY }}>
                    Recent System Activities
                  </h2>
                  <div className="divide-y divide-gray-50">
                    {recentActivities.map((a, i) => (
                      <ActivityRow key={i} {...a} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {quickActions.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {quickActions.map((a) => (
                <QuickActionCard key={a.title} {...a} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
